import { Injectable } from '@angular/core';
import { CaptureStorage, CapturedRequest } from './capture-storage';

interface GroupedCaptures {
  [endpoint: string]: CapturedRequest[];
}

@Injectable({ providedIn: 'root' })
export class CaptureExporter {
  constructor(private captureStorage: CaptureStorage) {}

  async exportAsJSON(): Promise<void> {
    const captures = await this.captureStorage.getAllCaptures();
    const grouped = this.groupByEndpoint(captures);

    // Create a blob and download
    const blob = new Blob([JSON.stringify(grouped, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `captured-responses-${new Date().toISOString().replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async exportToFileSystem(): Promise<void> {
    const captures = await this.captureStorage.getAllCaptures();
    const grouped = this.groupByEndpoint(captures);

    for (const [endpoint, requests] of Object.entries(grouped)) {
      await this.downloadEndpointFile(endpoint, requests);
      await this.delayToAvoidBrowserBlocking();
    }
  }

  private async downloadEndpointFile(endpoint: string, requests: CapturedRequest[]): Promise<void> {
    const fileName = this.sanitizeEndpointName(endpoint);
    const data = this.formatForHandlerGeneration(requests);
    const jsonContent = JSON.stringify(data, null, 2);

    this.triggerFileDownload(fileName, jsonContent);
  }

  private triggerFileDownload(fileName: string, content: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private async delayToAvoidBrowserBlocking(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  private groupByEndpoint(captures: CapturedRequest[]): GroupedCaptures {
    const grouped: GroupedCaptures = {};

    captures.forEach(capture => {
      const endpoint = this.extractEndpoint(capture.url);
      if (!grouped[endpoint]) {
        grouped[endpoint] = [];
      }
      grouped[endpoint].push(capture);
    });

    return grouped;
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      // Remove /v1/ or /api/v1/ prefix and extract main resource
      const cleaned = path.replace(/^\/(api\/)?v\d+\//, '');
      return cleaned.split('/').filter(s => s.length > 0)[0] || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private sanitizeEndpointName(endpoint: string): string {
    return endpoint.replace(/[^a-z0-9]/gi, '-').toLowerCase();
  }

  private formatForHandlerGeneration(requests: CapturedRequest[]): any {
    const grouped = this.groupRequestsByMethodAndUrl(requests);
    return Object.values(grouped);
  }

  private groupRequestsByMethodAndUrl(requests: CapturedRequest[]): Record<string, any> {
    const grouped: Record<string, any> = {};

    requests.forEach(req => {
      const key = this.createRequestKey(req);
      this.ensureGroupExists(grouped, key, req);
      this.addResponseToGroup(grouped[key], req);
    });

    return grouped;
  }

  private createRequestKey(request: CapturedRequest): string {
    return `${request.method} ${request.url}`;
  }

  private ensureGroupExists(grouped: Record<string, any>, key: string, request: CapturedRequest): void {
    if (!grouped[key]) {
      grouped[key] = {
        method: request.method,
        urlPattern: this.extractUrlPattern(request.url),
        responses: [],
      };
    }
  }

  private addResponseToGroup(group: any, request: CapturedRequest): void {
    group.responses.push({
      status: request.responseStatus,
      headers: request.responseHeaders,
      body: request.responseBody,
    });
  }

  private extractUrlPattern(url: string): string {
    try {
      const urlObj = new URL(url);
      // Replace IDs and GUIDs with placeholders
      let pattern = urlObj.pathname;
      pattern = pattern.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id');
      pattern = pattern.replace(/\/\d+/g, '/:id');
      // Strip query parameters - they should be handled in the handler, not the URL pattern
      return pattern;
    } catch {
      // Fallback: if not a full URL, just strip query params from the path
      return url.split('?')[0];
    }
  }
}
